/**
 * src/lib/api/crm.js — CRM read/write API.
 * Returns raw snake_case Supabase rows. See docs/SHAPE_CONTRACT.md.
 */

import { supabase } from '../supabase';
import { logEvent } from '../observability';
import { apiFetch, API_BASE } from './client';
import { makeEntityCache } from '../offline/entityCache';
import { getOnline } from '../offline/networkStatus';
import { thumbUrlFromDoc } from './documents';
import { linkedIds, unionById } from './vessel360';

const SELECT_DEAL = '*, last_email_at, emails_seen_at, user_profiles!owner_id(display_name), vessels!vessel_id(id, vessel_name, make_model, year, home_port_name, berth_number), project_types!project_type_id(id, slug, label_en, label_es), stages!stage_id(id, stage_name, label_en, label_es, color, slug), lead_emails!deal_id(id, direction, sent_at, from_email, to_email)'; // TICKET-211: unread email columns; from/to = pipeline search by sender

export async function fetchCRM() {
  try {
    const { data, error } = await supabase
      .from('crm_deals')
      .select(SELECT_DEAL)
      .is('archived_at', null)
      .order('created_at', { ascending: false });
    if (error) { console.warn('[crm] fetch error:', error.message); return []; }
    return data || [];
  } catch (err) {
    console.warn('[crm] fetchCRM failed:', err.message);
    return [];
  }
}

// TICKET-207 — fetch stage change history for a deal (for time-in-stage metric).
// Returns rows ordered oldest-first: [{from_stage_id, to_stage_id, from_status, to_status, changed_at}].
export async function fetchDealStageHistory(dealId) {
  if (!dealId) return [];
  try {
    const { data, error } = await supabase
      .from('deal_stage_history')
      .select('*')
      .eq('deal_id', dealId)
      .order('changed_at', { ascending: true });
    if (error) { console.warn('[crm] fetchDealStageHistory error:', error.message); return []; }
    return data || [];
  } catch (err) {
    console.warn('[crm] fetchDealStageHistory failed:', err.message); // spine-emit-ok: read-only frontend helper, graceful [] fallback
    return [];
  }
}

// Per-deal activity cache key so each deal keeps its own offline copy (the
// collection cache replaces the whole keyed set; keying by deal avoids one
// deal's fetch clobbering another's). Best-effort — a cache miss/error never
// degrades the live result.
function activitiesCache(dealId) {
  return makeEntityCache('crm_activities::' + dealId);
}

export async function fetchCRMActivities(dealId) {
  if (!dealId) return [];
  const online = getOnline();
  try {
    const { data, error } = await supabase
      .from('crm_activities')
      .select('*')
      .eq('deal_id', dealId)
      .order('planned_date', { ascending: false });
    if (error) {
      // On a read error, fall back to the offline cache so the timeline still
      // renders what we last saw (best-effort).
      const cached = await activitiesCache(dealId).read().catch(() => []);
      return cached.length ? cached : [];
    }
    const rows = data || [];
    // reconcile: online-with-rows writes the cache; offline / empty hydrates it.
    const { rows: out } = await activitiesCache(dealId)
      .reconcile({ fetched: rows, online })
      .catch(() => ({ rows }));
    return out;
  } catch {
    const cached = await activitiesCache(dealId).read().catch(() => []);
    return cached.length ? cached : [];
  }
}

// Writes still go through proxy (complex cascade logic in handlers)
export async function createCRMDeal(payload) {
  return apiFetch(`${API_BASE}/sync`, {
    method: 'POST',
    body: JSON.stringify({ action: 'create_crm_deal', ...payload }),
  });
}

export async function createCRMActivity(crmRecordId, type, details, purpose, plannedDate, contactId, isDecision, id) {
  return apiFetch(`${API_BASE}/sync`, {
    method: 'POST',
    body: JSON.stringify({ action: 'create_crm_activity', crm_id: crmRecordId, type, details, interaction_type: purpose, planned_date: plannedDate, contact_id: contactId || null, is_decision: !!isDecision, ...(id ? { id } : {}) }),
  });
}

// Object-arg path for the offline replay applier — maps a flat payload (carrying
// the client-supplied id + the crm_activities fields) onto the positional call.
// Keeps the client id threading clean without forcing every caller to the long
// positional form. See syncEngine DEFAULT_APPLIERS.crm.create_activity.
export async function createCRMActivityFromPayload(payload = {}) {
  const { id, deal_id, crm_id, type, details, interaction_type, purpose, planned_date, contact_id, is_decision } = payload;
  return createCRMActivity(
    crm_id || deal_id,
    type,
    details,
    interaction_type || purpose || null,
    planned_date || null,
    contact_id || null,
    is_decision,
    id,
  );
}

// TICKET-DEAL-HISTORY-UNIFY — inline-edit a CRM activity (body and/or type).
// Returns { success, record } where record is the raw supabase row.
export async function updateCRMActivity(recordId, { details, type, contact_id, is_decision } = {}) {
  return apiFetch(`${API_BASE}/sync`, {
    method: 'POST',
    body: JSON.stringify({ action: 'update_crm_activity', record_id: recordId, details, type, contact_id, is_decision }),
  });
}

// TICKET-DEAL-BRAIN-P3 — cross-project reference by product type (user-auth read;
// the server derives the role from the JWT and gates pricing accordingly).
export async function fetchProductReference(value) {
  const v = (value || '').trim();
  if (!v) return null;
  return apiFetch(`${API_BASE}/ai?action=brain.product_reference&value=${encodeURIComponent(v)}`);
}

export async function updateCRMStatus(crmRecordId, status) {
  return apiFetch(`${API_BASE}/sync`, {
    method: 'POST',
    body: JSON.stringify({ action: 'update_crm_status', record_id: crmRecordId, status }),
  });
}

/**
 * TICKET-211 — Mark a deal's emails as seen (clears the unread badge).
 * Direct Supabase write; best-effort (badge clearance is UI-state only).
 * @param {string} dealId
 * @returns {Promise<void>}
 */
export async function markDealEmailsSeen(dealId) {
  try {
    await supabase
      .from('crm_deals')
      .update({ emails_seen_at: new Date().toISOString() })
      .eq('id', dealId);
  } catch {
    // spine-emit-ok: badge clear is UI-only best-effort; reverting on next fetch is acceptable, no user-visible data loss
  }
}

export async function updateCRMDeal(recordId, fields) {
  return apiFetch(`${API_BASE}/sync`, {
    method: 'POST',
    body: JSON.stringify({ action: 'update_crm_deal', record_id: recordId, ...fields }),
  });
}

export async function fetchDealsForSelector() {
  try {
    // Filter out terminal deals via stage join (is_terminal=true covers Won/Lost/Ganada/Perdida)
    const { data, error } = await supabase
      .from('crm_deals')
      .select('id, opportunity, status, project_value, vessel_id, vessels!vessel_id(vessel_name), stages!stage_id(is_terminal, slug)')
      .order('updated_at', { ascending: false })
      .limit(200);
    if (error) { console.warn('[crm] fetchDealsForSelector error:', error.message); return []; }
    // Exclude deals whose stage is terminal (won/lost) — handles any language
    return (data || []).filter(d => !d.stages?.is_terminal);
  } catch (err) {
    console.warn('[crm] fetchDealsForSelector failed:', err.message);
    return [];
  }
}

export async function fetchClients() {
  try {
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .order('client_name');
    if (error) return [];
    return data || [];
  } catch { return []; }
}

export async function fetchVessels() {
  try {
    // TICKET-292: archived (merged-away duplicate) vessels leave the directory.
    // Migration-safe — if `archived_at` is not live yet, fall back to the
    // unfiltered fetch so the directory never blanks when this deploys ahead of
    // the column (committed ≠ applied; the filter self-activates on apply).
    let { data, error } = await supabase
      .from('vessels')
      .select('*')
      .is('archived_at', null)
      .order('vessel_name');
    if (error && /archived_at|column/i.test(error.message || '')) {
      ({ data, error } = await supabase.from('vessels').select('*').order('vessel_name'));
    }
    if (error) { console.warn('[vessels] fetch error:', error.message); return []; }
    return data || [];
  } catch (err) {
    console.warn('[vessels] fetchVessels failed:', err.message);
    return [];
  }
}

// TICKET-705 — company-first deal creation: search a client, list the boats
// it owns/manages (vessel_clients junction), then the boat's contacts. Mirrors
// the vessel_clients join already used in fetchContactHistory.
export async function fetchVesselsForClient(clientId) {
  if (!clientId) return [];
  try {
    const { data, error } = await supabase
      .from('vessel_clients')
      .select('vessel_id, relationship, vessels!vessel_id(id, vessel_name, make_model, year)')
      .eq('client_id', clientId)
      .eq('is_current', true);
    if (error) return [];
    return (data || []).map(row => ({ ...row.vessels, relationship: row.relationship })).filter(v => v?.id);
  } catch { /* spine-emit-ok: read-only frontend helper, graceful [] fallback */ return []; }
}

// TICKET-705 — the inverse lookup: given a vessel (e.g. a deal already carries
// vessel_id), resolve its linked company so "missing company info on the deal"
// can be shown without a crm_deals schema change (client_id was tried and
// reverted — TICKET-484).
export async function fetchClientForVessel(vesselId) {
  if (!vesselId) return null;
  try {
    const { data, error } = await supabase
      .from('vessel_clients')
      .select('client_id, relationship, clients!client_id(id, client_name, company_name, email, phone, address)')
      .eq('vessel_id', vesselId)
      .eq('is_current', true)
      .order('is_billing_party', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error || !data?.clients) return null;
    return { ...data.clients, relationship: data.relationship };
  } catch { /* spine-emit-ok: read-only frontend helper, graceful null fallback */ return null; }
}

// TICKET-853 — every company linked to a boat, current first. `fetchClientForVessel`
// answers "which ONE company" for a compact display; this is the full list the
// company panel edits (a boat can have an owner company AND a management company).
export async function fetchVesselClients(vesselId) {
  if (!vesselId) return [];
  try {
    const { data, error } = await supabase
      .from('vessel_clients')
      .select('*, clients!client_id(id, client_name, company_name, email, phone), form_vessel_roles!role_id(id, slug, label_en, label_es)')
      .eq('vessel_id', vesselId)
      .order('is_current', { ascending: false })
      .order('created_at', { ascending: true });
    if (error) return [];
    return data || [];
  } catch { /* spine-emit-ok: read-only frontend helper, graceful [] fallback */ return []; }
}

// TICKET-853 — the roles a COMPANY can hold on a boat, from the registry
// (`form_vessel_roles`), never a hardcoded list. `is_company_role` already marks
// management/charter companies; `owner` joins them because a boat's owner is
// frequently a holding company rather than a person.
export async function fetchVesselCompanyRoles() {
  try {
    const { data, error } = await supabase
      .from('form_vessel_roles')
      .select('id, slug, label_en, label_es, is_company_role')
      .eq('is_active', true)
      .or('is_company_role.eq.true,slug.eq.owner')
      .order('sort_order');
    if (error) return [];
    return data || [];
  } catch { /* spine-emit-ok: read-only frontend helper, graceful [] fallback */ return []; }
}

// TICKET-853 — how many current contacts each boat in a fleet has, in ONE query
// rather than one per boat. Returns a { vessel_id: count } map.
export async function fetchContactCountsForVessels(vesselIds = []) {
  const ids = (vesselIds || []).filter(Boolean);
  if (ids.length === 0) return {};
  try {
    const { data, error } = await supabase
      .from('vessel_contacts')
      .select('vessel_id')
      .is('archived_at', null)
      .in('vessel_id', ids)
      .neq('is_current', false);
    if (error) return {};
    return (data || []).reduce((acc, r) => {
      acc[r.vessel_id] = (acc[r.vessel_id] || 0) + 1;
      return acc;
    }, {});
  } catch { /* spine-emit-ok: read-only frontend helper, graceful {} fallback */ return {}; }
}

// TICKET-853 — the write half of the vessel <-> company junction. Idempotent on
// (vessel_id, client_id, relationship), so re-linking updates rather than forks.
export async function linkVesselClient({ vessel_id, client_id, relationship, is_billing_party, notes }) {
  return apiFetch(`${API_BASE}/sync`, {
    method: 'POST',
    body: JSON.stringify({ action: 'link_vessel_client', vessel_id, client_id, relationship, is_billing_party, notes }),
  });
}

// Closes the link (is_current false + date_to) — never deletes, so the history of
// who managed a boat and when survives (root CLAUDE.md §SAFETY NET).
export async function unlinkVesselClient(id) {
  return apiFetch(`${API_BASE}/sync`, {
    method: 'POST',
    body: JSON.stringify({ action: 'unlink_vessel_client', id }),
  });
}

export async function fetchVesselDetail(vesselId) {
  if (!vesselId) return null;
  try {
    const { data, error } = await supabase
      .from('vessels')
      .select('*')
      .eq('id', vesselId)
      .single();
    if (error) return null;
    return data;
  } catch { return null; }
}

// Synthesize the display fields (`contact_name`, `role`) consumers expect.
// Base table has first_name/last_name (separate) and roles[] (array); the
// `vessel_full_context` SQL view derives these for agents. Frontend reads
// vessel_contacts directly, so we mirror that derivation client-side.
function deriveContactDisplay(row) {
  if (!row) return row;
  const first = (row.first_name || '').trim();
  const last = (row.last_name || '').trim();
  const fullName = [first, last].filter(Boolean).join(' ');
  return {
    ...row,
    contact_name: row.contact_name || fullName || null,
    role: row.role || (Array.isArray(row.roles) && row.roles.length ? row.roles[0] : null),
  };
}

export async function fetchVesselContacts(vesselId) {
  if (!vesselId) return [];
  try {
    const { data, error } = await supabase
      .from('vessel_contacts')
      .select('*')
      .is('archived_at', null)
      .eq('vessel_id', vesselId)
      .order('is_current', { ascending: false });
    if (error) return [];
    return (data || []).map(deriveContactDisplay);
  } catch { return []; }
}

// Non-Vessel Lead Epic Phase 2: contacts on a deal that has no vessel (restaurant/
// hotel/bank/…). Reads the same vessel_contacts store by deal_id (RLS select=true).
export async function fetchDealContacts(dealId) {
  if (!dealId) return [];
  try {
    const { data, error } = await supabase
      .from('vessel_contacts')
      .select('*')
      .is('archived_at', null)
      .eq('deal_id', dealId)
      .order('is_current', { ascending: false });
    if (error) return [];
    return (data || []).map(deriveContactDisplay);
  } catch {
    // spine-emit-ok: best-effort client read; degrades to [] on a transient error (mirrors fetchVesselContacts)
    return [];
  }
}

export async function fetchAllVesselContacts() {
  try {
    const { data, error } = await supabase
      .from('vessel_contacts')
      .select('*')
      .is('archived_at', null)
      .order('is_current', { ascending: false });
    if (error) { console.warn('[vessel_contacts] fetch error:', error.message); return []; }
    return (data || []).map(deriveContactDisplay);
  } catch (err) {
    console.warn('[vessel_contacts] fetchAllVesselContacts failed:', err.message);
    return [];
  }
}

/**
 * fetchVesselHistory(vesselId) — single-call aggregate of EVERYTHING attached to a vessel.
 *
 * Returns:
 * {
 *   vessel:        vessels row + { contacts, documents } embedded
 *   deals:         crm_deals rows where vessel_id = X
 *   projects:      projects rows where vessel_id = X (with stage joined)
 *   bundles:       project_bundles rows where vessel_id = X
 *   voiceRecords:  voice_records rows where vessel_id = X
 *   files:         project_files rows where vessel_id = X
 *   documents:     vessel_documents rows where vessel_id = X
 *   timeline:      sorted union of events with { type, date, title, ref, raw }
 * }
 *
 * One network round-trip via Promise.all. Errors on any branch return [] for that branch.
 */
export async function fetchVesselHistory(vesselId) {
  if (!vesselId) return null;
  const safe = async (p) => {
    try { const { data, error } = await p; if (error) return []; return data || []; }
    catch { return []; }
  };
  const DOC_COLS = 'id, file_name, file_path, mime_type, document_type, summary, document_metadata, project_id, deal_id, created_at';
  const PROJECT_COLS = '*, stages!stage_id(id, stage_name, label_en, label_es, color, slug), project_types!project_type_id(id, slug, label_en, label_es)';
  const INVOICE_COLS = 'id, invoice_number, issue_date, due_date, amount_eur, base_amount, iva_amount, status, matched_bank_transaction_id, deal_id, project_fk, vessel_id, client_name, created_at';
  const [vessel, rawContacts, docsByVessel, dealsByVessel, projectsByVessel, bundlesByVessel, voiceRecords, files, quotesByVessel, invoicesByVessel] = await Promise.all([
    (async () => {
      try {
        const { data } = await supabase.from('vessels').select('*').eq('id', vesselId).single();
        return data || null;
      } catch { return null; }
    })(),
    safe(supabase.from('vessel_contacts').select('*').is('archived_at', null).eq('vessel_id', vesselId).order('is_current', { ascending: false })),
    // TICKET-459 — this read `vessel_documents`, which has held ZERO rows in prod
    // since it was created, so the Directory's Documents tab was empty BY
    // CONSTRUCTION while all 313 real documents sat in `project_documents`.
    // Reading the real store is what makes the boat the one place everything is
    // visible from: schematics filed on a deal, photos filed on a project, and
    // email attachments all carry vessel_id, so they federate here after the
    // deal/project closes (CEO 2026-07-27).
    safe(supabase.from('project_documents')
      .select(DOC_COLS)
      .eq('vessel_id', vesselId).is('archived_at', null).order('created_at', { ascending: false })),
    safe(supabase.from('crm_deals').select(SELECT_DEAL).eq('vessel_id', vesselId).order('created_at', { ascending: false })),
    safe(supabase.from('projects').select(PROJECT_COLS).eq('vessel_id', vesselId).order('created_at', { ascending: false })),
    safe(supabase.from('project_bundles').select('*').eq('vessel_id', vesselId).is('archived_at', null).order('created_at', { ascending: false })),
    safe(supabase.from('voice_records').select('*').eq('vessel_id', vesselId).order('created_at', { ascending: false })),
    // 2026-04-20 Phase 1 — project_files now supports soft-archive; exclude archived rows.
    safe(supabase.from('project_files').select('*').eq('vessel_id', vesselId).is('archived_at', null).order('created_at', { ascending: false })),
    // TICKET-1331: quotes only to discover linked deals (RLS: ceo/admin/sales/pmo).
    safe(supabase.from('quotes').select('id, deal_id, project_id').eq('vessel_id', vesselId).is('archived_at', null)),
    // TICKET-1331: sales invoices (RLS: ceo/admin only -- other roles read none).
    safe(supabase.from('invoices').select(INVOICE_COLS).eq('vessel_id', vesselId).is('archived_at', null)),
  ]);

  // TICKET-1331 (Vessel 360): rows linked to the boat only THROUGH a deal or project
  // (bundles with vessel_id NULL, signed PDFs filed on the deal) -- read by those ids,
  // then union. Dama de Noche 2026-10-19: 15 of 18 bundles were invisible by vessel_id.
  const { dealIds, projectIds } = linkedIds({ deals: dealsByVessel, projects: projectsByVessel, quotes: quotesByVessel });
  const byIds = (table, cols, col, ids, archived = true) => {
    if (!ids.length) return Promise.resolve([]);
    try {
      let q = supabase.from(table).select(cols).in(col, ids);
      if (archived) q = q.is('archived_at', null);
      return safe(q);
    } catch (e) {
      // a missing link never blanks the boat page -- but it is logged, never silent
      logEvent('vessel_history_linked_query_failed', e?.message || 'unknown', { vesselId, table, col });
      return Promise.resolve([]);
    }
  };
  const knownDeals = new Set(dealsByVessel.map((d) => String(d.id)));
  const knownProjects = new Set(projectsByVessel.map((p) => String(p.id)));
  const [dealsLinked, projectsById, projectsByDeal, bundlesByProj, bundlesByDeal, bundlesByDealFk, docsByDeal, docsByProject, invoicesByDeal, invoicesByProject] = await Promise.all([
    byIds('crm_deals', SELECT_DEAL, 'id', dealIds.filter((id) => !knownDeals.has(id)), false),
    byIds('projects', PROJECT_COLS, 'id', projectIds.filter((id) => !knownProjects.has(id)), false),
    byIds('projects', PROJECT_COLS, 'deal_id', dealIds, false),
    byIds('project_bundles', '*', 'project_fk', projectIds),
    byIds('project_bundles', '*', 'crm_deal_id', dealIds),
    byIds('project_bundles', '*', 'deal_fk', dealIds),
    byIds('project_documents', DOC_COLS, 'deal_id', dealIds),
    byIds('project_documents', DOC_COLS, 'project_id', projectIds),
    byIds('invoices', INVOICE_COLS, 'deal_id', dealIds),
    byIds('invoices', INVOICE_COLS, 'project_fk', projectIds),
  ]);
  const byNewest = (a, b) => String(b.created_at || '').localeCompare(String(a.created_at || ''));
  const deals = unionById(dealsByVessel, dealsLinked).sort(byNewest);
  const projects = unionById(projectsByVessel, projectsById, projectsByDeal).sort(byNewest);
  const bundles = unionById(bundlesByVessel, bundlesByProj, bundlesByDeal, bundlesByDealFk).sort(byNewest);
  const rawDocuments = unionById(docsByVessel, docsByDeal, docsByProject).sort(byNewest);
  const invoices = unionById(invoicesByVessel, invoicesByDeal, invoicesByProject)
    .sort((a, b) => String(b.issue_date || b.created_at || '').localeCompare(String(a.issue_date || a.created_at || '')));

  const contacts = (rawContacts || []).map(deriveContactDisplay);

  // Normalise to the shape the Directory renders (title/uploaded_at/file_url),
  // keeping the raw row so a tile can open the file through the backend-agnostic
  // resolver. file_url is the PUBLIC thumb where one exists — a photo tile must
  // never pull the full-resolution original, and non-photos have no public URL
  // at all, so they open on tap instead of via a dead href.
  const documents = (rawDocuments || []).map(r => ({
    id: r.id,
    document_type: r.document_type || 'other',
    title: r.file_name || r.summary || 'Document',
    uploaded_at: r.created_at,
    file_url: thumbUrlFromDoc(r),
    raw: r,
  }));

  // Build chronological timeline from all sources
  const timeline = [];
  for (const d of deals) {
    // canonical field: crm_deals.opportunity (migration applied)
    timeline.push({ type: 'dealCreated', date: d.created_at, title: d.opportunity || d.id, refId: d.id, refTable: 'crm_deals', raw: d });
  }
  for (const p of projects) {
    timeline.push({ type: 'projectStarted', date: p.created_at, title: p.project_name || p.id, refId: p.id, refTable: 'projects', raw: p });
  }
  for (const b of bundles) {
    // canonical fields: project_bundles.title, completed_at (migration applied)
    timeline.push({ type: 'bundleCompleted', date: b.completed_at || b.created_at, title: b.title || b.id, refId: b.id, refTable: 'project_bundles', raw: b });
  }
  for (const v of voiceRecords) {
    // canonical fields: voice_records.headline, summary (migration applied)
    timeline.push({ type: 'voiceRecorded', date: v.created_at, title: v.headline || v.summary || 'Voice note', refId: v.id, refTable: 'voice_records', raw: v });
  }
  for (const doc of documents) {
    timeline.push({ type: 'documentUploaded', date: doc.uploaded_at, title: doc.title || 'Document', refId: doc.id, refTable: 'project_documents', raw: doc.raw });
  }
  for (const f of files) {
    timeline.push({ type: 'documentUploaded', date: f.created_at, title: f.file_name || f.title || 'File', refId: f.id, refTable: 'project_files', raw: f });
  }
  for (const c of contacts) {
    if (c.created_at) {
      timeline.push({ type: 'contactChanged', date: c.created_at, title: c.contact_name || c.role || 'Contact', refId: c.id, refTable: 'vessel_contacts', raw: c });
    }
  }
  timeline.sort((a, b) => {
    const da = a.date ? new Date(a.date).getTime() : 0;
    const db = b.date ? new Date(b.date).getTime() : 0;
    return db - da;
  });

  return {
    vessel: vessel ? { ...vessel, contacts, documents } : null,
    deals,
    projects,
    bundles,
    voiceRecords,
    files,
    documents,
    invoices,
    timeline,
  };
}

// TICKET-649 - contact-centric detail history, mirrors fetchVesselHistory's
// fan-out-then-merge shape. A vessel_contacts row is the CRM "contact"; deals
// and projects surface for its linked vessel (contact.vessel_id), peer crew
// come from other vessel_contacts rows on the same vessel, and the
// management company comes from vessel_clients. A contact with no vessel_id
// (independent broker) short-circuits to overview-only.
export async function fetchContactHistory(contactId) {
  if (!contactId) return null;
  // Secondary history lists degrade to [] but never silently (TICKET-1044).
  const safe = async (p) => {
    try {
      const { data, error } = await p;
      if (error) { logEvent('contact_history_subquery_failed', error.message || 'unknown', { contactId }); return []; }
      return data || [];
    } catch (e) { logEvent('contact_history_subquery_failed', e?.message || 'unknown', { contactId }); return []; }
  };

  // TICKET-1044: PGRST116 = no row (genuine not-found -> null). Any other failure
  // rethrows so the view can show its error state instead of "not found".
  const { data: contact, error: contactErr } = await supabase.from('vessel_contacts').select('*').is('archived_at', null).eq('id', contactId).single();
  if (contactErr && contactErr.code !== 'PGRST116') throw contactErr;

  if (!contact) return null;

  if (!contact.vessel_id) {
    return { contact, vessel: null, deals: [], projects: [], peerContacts: [], managementLinks: [], timeline: [] };
  }

  const [vessel, deals, projects, peerContacts, managementLinks] = await Promise.all([
    (async () => {
      try {
        const { data } = await supabase.from('vessels').select('*').eq('id', contact.vessel_id).single();
        return data || null;
      } catch (e) { logEvent('contact_history_subquery_failed', e?.message || 'unknown', { contactId }); return null; }
    })(),
    safe(supabase.from('crm_deals').select(SELECT_DEAL).eq('vessel_id', contact.vessel_id).order('created_at', { ascending: false })),
    safe(supabase.from('projects').select('*, stages!stage_id(id, stage_name, label_en, label_es, color, slug), project_types!project_type_id(id, slug, label_en, label_es)').eq('vessel_id', contact.vessel_id).order('created_at', { ascending: false })),
    safe(supabase.from('vessel_contacts').select('*').is('archived_at', null).eq('vessel_id', contact.vessel_id).neq('id', contactId).order('is_current', { ascending: false })),
    safe(supabase.from('vessel_clients').select('*, clients!client_id(id, client_name, company_name, email, phone)').eq('vessel_id', contact.vessel_id).order('is_current', { ascending: false })),
  ]);

  const timeline = [];
  for (const d of deals) {
    timeline.push({ type: 'dealCreated', date: d.created_at, title: d.opportunity || d.id, refId: d.id, refTable: 'crm_deals', raw: d });
  }
  for (const p of projects) {
    timeline.push({ type: 'projectStarted', date: p.created_at, title: p.project_name || p.id, refId: p.id, refTable: 'projects', raw: p });
  }
  if (contact.date_from) {
    timeline.push({ type: 'roleStarted', date: contact.date_from, title: contact.role || contact.contact_name || '', refId: contact.id, refTable: 'vessel_contacts', raw: contact });
  }
  if (contact.date_to) {
    timeline.push({ type: 'roleEnded', date: contact.date_to, title: contact.role || contact.contact_name || '', refId: contact.id, refTable: 'vessel_contacts', raw: contact });
  }
  timeline.sort((a, b) => {
    const da = a.date ? new Date(a.date).getTime() : 0;
    const db = b.date ? new Date(b.date).getTime() : 0;
    return db - da;
  });

  return { contact, vessel, deals, projects, peerContacts, managementLinks, timeline };
}

export async function createVessel(data) {
  const { data: created, error } = await supabase
    .from('vessels')
    .insert(data)
    .select()
    .single();
  if (error) return { success: false, error: error.message };
  return { success: true, record: created };
}

export async function createVesselContact(data) {
  return apiFetch(`${API_BASE}/sync`, {
    method: 'POST',
    body: JSON.stringify({ action: 'create_vessel_contact', ...data }),
  });
}

export async function updateVesselContact(id, data) {
  return apiFetch(`${API_BASE}/sync`, {
    method: 'POST',
    body: JSON.stringify({ action: 'update_vessel_contact', id, ...data }),
  });
}

export async function deleteVesselContact(id) {
  return apiFetch(`${API_BASE}/sync`, {
    method: 'POST',
    body: JSON.stringify({ action: 'delete_vessel_contact', id }),
  });
}

export async function updateVessel(id, data) {
  return apiFetch(`${API_BASE}/sync`, {
    method: 'POST',
    body: JSON.stringify({ action: 'update_vessel', id, ...data }),
  });
}

export async function fetchPorts() {
  const { data } = await supabase.from('ports').select('*').order('port_name');
  return data || [];
}

export async function createPort(data) {
  return apiFetch(`${API_BASE}/sync`, {
    method: 'POST',
    body: JSON.stringify({ action: 'create_port', ...data }),
  });
}

export async function fetchTasksByIds(taskIds) {
  if (!taskIds?.length) return [];
  try {
    const { data, error } = await supabase
      .from('tasks')
      .select('*')
      .in('id', taskIds);
    if (error) return [];
    return data || [];
  } catch { return []; }
}
